import * as React from 'react';
import { Box } from '@mui/material';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

export type ScrollbarProps = {
    children: React.ReactNode;
    style?: React.CSSProperties;
    onReachTop?: () => void;
    onReachBottom?: () => void;
};

export type ScrollbarHandle = {
    scrollToTop: () => void;
};

export const Scrollbar = React.forwardRef<ScrollbarHandle, ScrollbarProps>(
    ({ children, style, onReachTop, onReachBottom }, ref) => {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
        );
        const containerRef = React.useRef<HTMLElement | null>(null);

        React.useImperativeHandle(ref, () => ({
            scrollToTop: () => {
                if (containerRef.current) {
                    containerRef.current.scrollTop = 0;
                }
            },
        }));

        const handleScroll = (el: HTMLElement) => {
            const { scrollTop } = el;
            const { scrollHeight } = el;
            const { clientHeight } = el;

            const tolerance = 10; // px de marge pour éviter les faux positifs

            if (scrollTop <= tolerance) {
                onReachTop?.();
            } else if (scrollTop + clientHeight >= scrollHeight - tolerance) {
                onReachBottom?.();
            }
        };

        const onMobileScroll = (event: React.UIEvent<HTMLDivElement>) => {
            handleScroll(event.currentTarget);
        };

        return isMobile ? (
            <Box
                ref={containerRef}
                data-testid="test-id-Scrollbar"
                sx={{
                    overflowX: 'auto',
                    overflowY: 'auto',
                }}
                onScroll={onMobileScroll}
            >
                {children}
            </Box>
        ) : (
            <PerfectScrollbar
                role="scrollbar"
                style={style}
                containerRef={(refDom) => {
                    if (refDom) {
                        containerRef.current = refDom;
                        const scrollContainer = refDom;
                        scrollContainer.onscroll = () => handleScroll(scrollContainer);
                    }
                }}
            >
                {children}
            </PerfectScrollbar>
        );
    },
);
